'use client';

import { MenuLinkSliceWithSubMenu } from '../../../prismicio-types';
import { SliceZone } from '@prismicio/react';
import { getNavigationComponents } from '@/slices/component-factory';
import { useMenuContext } from '@/context/MenuContext';
import { Content } from '@prismicio/client';
import { createPortal } from 'react-dom';
import { useEffect, useMemo, useState } from 'react';
import { useIsClient } from '@/hooks';

export type PopOverMenuProps = {
  slice: MenuLinkSliceWithSubMenu;
  context:
    | {
        [key: string]: Content.SubmenuSlice[];
      }
    | undefined;
  isHovered: boolean;
  showMenu: () => void;
  hideMenu: () => void;
  menuItemRef: React.RefObject<HTMLLIElement | null>;
};

export const PopOverMenu = ({
  slice,
  context,
  isHovered,
  showMenu,
  hideMenu,
  menuItemRef,
}: PopOverMenuProps) => {
  const { isMobile, popOverStates } = useMenuContext();
  const mounted = useIsClient();
  const [menuPosition, setMenuPosition] = useState({ left: 0, width: 0 });
  const navigationComponents = useMemo(() => getNavigationComponents(), []);
  const isPopOverMenuOpen = popOverStates[slice.primary.tag!];
  const submenuSlices = context?.[slice.primary.tag!];
  const firstSubmenuSlice = submenuSlices?.[0];

  const isModernSubmenu =
    firstSubmenuSlice &&
    'slice_type' in firstSubmenuSlice &&
    firstSubmenuSlice.slice_type === 'submenu' &&
    'variation' in firstSubmenuSlice &&
    firstSubmenuSlice.variation === 'modernSubmenu';

  useEffect(() => {
    const updateMenuPosition = () => {
      if (menuItemRef.current && !isMobile) {
        const rect = menuItemRef.current.getBoundingClientRect();
        setMenuPosition({
          left: rect.left + rect.width * 0.9, // Position at 75% of the menu item width (towards the arrow)
          width: rect.width,
        });
      }
    };

    updateMenuPosition();
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition);

    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition);
    };
  }, [menuItemRef, isMobile, isHovered]);

  const renderSliceZone = () => (
    <SliceZone
      slices={context?.[slice.primary.tag!]}
      components={navigationComponents}
      context={context}
    />
  );

  if (isMobile) {
    return (
      <div
        className={`${isPopOverMenuOpen ? '' : 'hidden'} bg-white bg-opacity-10 px-5`}
      >
        <div className="mt-6 mb-6 w-full pointer-events-auto">
          <div className="flex flex-col justify-between gap-12">
            {renderSliceZone()}
          </div>
        </div>
      </div>
    );
  }

  const modernMenuContainerClasses =
    'fixed top-[85px] z-30 will-change-transform will-change-opacity';
  const modernMenuTransitionClasses = `${
    isHovered
      ? 'opacity-100 translate-y-0 pointer-events-auto'
      : 'opacity-0 -translate-y-2 pointer-events-none'
  } transition-all duration-200 ease-out`;

  const defaultMenuContainerClasses = `lg:ml-0 w-full fixed inset-0 h-80 transform duration-300`;
  const defaultMenuTransitionClasses = `${
    isHovered
      ? 'visible opacity-100 translate-y-0'
      : 'invisible opacity-0 -translate-y-full'
  } transition-all duration-300`;

  // Prerendered modern submenu portal - always rendered with blur effects ready
  const modernSubmenuPortal =
    isModernSubmenu && mounted && typeof window !== 'undefined'
      ? createPortal(
          <div
            className={`${modernMenuContainerClasses} ${modernMenuTransitionClasses}`}
            style={{
              backgroundColor: 'transparent',
              left: `${menuPosition.left}px`,
              transform: 'translateX(-50%)', // Center the dropdown at the calculated position
            }}
          >
            <div
              onMouseEnter={showMenu}
              onMouseLeave={hideMenu}
              className="relative"
            >
              <div className="absolute -top-2 left-1/2 -transform -translate-x-1/2 w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-b-[10px] border-b-white/10" />
              {/* Optimized frosted glass effect - PRERENDERED for smooth transitions */}
              <div className="relative rounded-2xl overflow-hidden">
                {/* Primary backdrop blur layer */}
                <div className="absolute inset-0 bg-black/5 backdrop-blur-[47.5px]" />
                {/* Foreground container: strongest blur, border, and shadow for glass edge */}
                <div className="relative backdrop-blur-[47.5px] backdrop-filter border border-white/15 shadow-[0_0_0_1px_rgba(255,255,255,0.05)] px-8 py-6 rounded-2xl bg-transparent">
                  {renderSliceZone()}
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )
      : null;

  // Return the portal for modern submenus
  if (isModernSubmenu) {
    return modernSubmenuPortal;
  }

  // Fallback for non-modern submenus or during SSR
  return (
    <div
      className={
        isModernSubmenu
          ? `${modernMenuContainerClasses} ${modernMenuTransitionClasses}`
          : `${defaultMenuContainerClasses} ${defaultMenuTransitionClasses}`
      }
    >
      {isModernSubmenu ? null : (
        <div
          onMouseEnter={showMenu}
          onMouseLeave={hideMenu}
          className="relative bg-menu-gradient text-white w-full h-full px-10 py-6 text-left flex items-center justify-center mt-[75px]"
        >
          <div className="flex flex-col lg:flex-row justify-between 2xl:justify-center gap-16 w-full h-full overflow-hidden">
            {renderSliceZone()}
          </div>
        </div>
      )}
    </div>
  );
};
